<script setup lang="ts">
import dayjs from "dayjs";
import { computed, reactive, ref, watch } from "vue";
import {
  ElForm,
  type FormRules,
  ElFormItem,
  ElInput,
  ElDivider,
  ElSelect,
  ElOption,
  type FormInstance,
  type FormItemInstance,
} from "element-plus";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import { useRootStore } from "@/stores";
import {
  FIELD_TYPES,
  SAVELOADING,
  VISIT_TYPE,
  STATUS,
} from "@/stores/constants";
import { useLocationSelector } from "@/composables/location";
import { formatFullName } from "@/utils/string";
import { VisitFieldTypes, type ExpandedUser } from "@/api/zoolander";
import {
  type VisitorFieldValues,
  useUpdateVisitor,
  type UpdateVisitorPayload,
  type VisitWithFields,
} from "@/api/visits";
import type { VisitorField } from "@/pages/settings/visitors/fields/types";
import { useGetLocations } from "@/api/locations";
import { useGetVisitorCategories } from "@/api/visitorCategories";
import { useGetCompanySettings, type CompanySettings } from "@/api/companies";
import HostSelector from "./HostSelector.vue";
import EditVisitorTimelineFormSkeleton from "./EditVisitorTimelineFormSkeleton.vue";
import SaveDialog from "@/components/common/SaveDialog/SaveDialog.vue";
import InOutImage from "@/components/common/InOutImage/InOutImage.vue";
import VisitorFields from "./VisitorFields.vue";
import VisitMetadata from "@/pages/visitors/components/VisitMetadata.vue";
import ReadOnlyField from "@/components/common/ReadOnlyField/ReadOnlyField.vue";
import { useNotification } from "@/composables/useNotification";
import { useGetVisitorFields } from "@/api/visitorFields";
import { useValidation } from "@/composables/validation";
import { useDebounceFn } from "@vueuse/core";
import { useVisits } from "@/composables/visits";
import { useDateUtils } from "@/composables/dates";

export interface Props {
  visit?: VisitWithFields;
  isLoadingVisit: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { TYPE_TIMELINE } = VISIT_TYPE;
const { t, locale } = useI18n();
const { duration } = useVisits();
const { formatDateTime } = useDateUtils();
const { validateRange, required, validateFormItem } = useValidation();
const getCompanySettings = useGetCompanySettings();
const rootStore = useRootStore();
const { sendErrorNotification } = useNotification();
const { locationGroups } = useLocationSelector();
const getLocations = useGetLocations();

const UNSUPPORTED_FIELDS = [FIELD_TYPES.DOCUMENT, FIELD_TYPES.VACCINE_PASS];

const defaultForm = () => ({
  name: "",
  locationPk: "",
  locationName: "",
  categoryName: "",
  category: "",
  selectHost: false,
  hostName: "",
  hostId: "",
  companyName: "",
  metadata: {
    notes: "",
  },
});

const form = reactive(defaultForm());
const formOriginal = reactive(defaultForm());
const visitFields = reactive<VisitorField[]>([]);
const fieldValues = reactive<VisitorFieldValues>({});
const fieldValuesOriginal = reactive<VisitorFieldValues>({});
const editTimelineFormRef = ref<FormInstance>();
const nameRef = ref<FormItemInstance>();
const companyNameRef = ref<FormItemInstance>();
const saveLoading = ref<boolean | undefined>(SAVELOADING.SUCCESS);

const settings = computed<Partial<CompanySettings>>(
  () => getCompanySettings.data.value ?? {}
);

const visitingEmployeeToggle = computed(
  () => settings.value?.visiting_employee_toggle
);

const enabledInCategory = computed(
  () => !selectedCategory.value?.settings?.employee_selection
);

const hasHostSelection = computed(() =>
  Boolean(visitingEmployeeToggle.value && enabledInCategory.value)
);

const locations = computed(() => getLocations.data.value ?? []);

const locationGroup = computed(() =>
  locationGroups.value.find((group) =>
    group.locations.map(({ id }) => id).includes(form.locationPk)
  )
);

const getVisitorCategories = useGetVisitorCategories(locationGroup);

const getVisitorFields = useGetVisitorFields({
  visitorCategoryId: computed(() => form.category),
  locationGroupId: locationGroup.value?.id,
});

const { mutate: updateVisitorAsync } = useUpdateVisitor(() => {
  rootStore.setHasUnsavedFormChanges(false);
  saveLoading.value = SAVELOADING.SUCCESS;
});

const categories = computed(() => getVisitorCategories.data.value || []);

const selectedCategory = computed(() =>
  categories.value.find((category) => category.name === form.categoryName)
);

const companyNameState = computed(
  () => selectedCategory.value?.settings.company_field_state
);

const companyNameEnabled = computed(
  () =>
    (selectedCategory.value &&
      ["required", "optional"].includes(companyNameState.value ?? "")) ||
    form.companyName
);

const companyNameRequired = computed(
  () => companyNameState.value === "required"
);

const companyNameRules = computed(() =>
  companyNameRequired.value ? [required(), validateRange(1, 128)] : []
);

const rules = computed<FormRules>(() => ({
  name: [required(), validateRange(1, 128)],
  categoryName: [required()],
  companyName: companyNameRules.value,
}));

const changesExist = computed(() => {
  const formChanges = (Object.keys(form) as Array<keyof typeof form>).some(
    (key) => {
      if (
        key === "metadata" &&
        form.metadata.notes !== formOriginal.metadata.notes
      ) {
        return true;
      }

      return String(form[key]) !== String(formOriginal[key]);
    }
  );

  if (formChanges) return true;

  const fieldsChanges = (
    Object.keys(fieldValues) as Array<keyof typeof fieldValues>
  ).some((key) => {
    const field = getVisitorFields.data.value?.find(
      (field) => field.numericId === Number(key) && field.active
    );

    if (!field) return false;

    if (field?.fieldType === FIELD_TYPES.MULTISELECT) {
      return (
        String(((fieldValues[key] as string[]) || []).sort()) !==
        String(((fieldValuesOriginal[key] as string[]) || []).sort())
      );
    }

    return (
      String(fieldValues[key] ?? "") !== String(fieldValuesOriginal[key] ?? "")
    );
  });

  return fieldsChanges;
});

const saveDialogVisible = computed(() => changesExist.value);

const isAnonymized = computed(() => Boolean(props.visit?.anonymizedAt));

const isLoading = computed(
  () =>
    props.isLoadingVisit ||
    getVisitorCategories.isLoading.value ||
    getCompanySettings.isLoading.value
);

const setHost = (host: ExpandedUser) => {
  form.hostId = host.id;
  form.hostName = formatFullName(host.firstName, host.lastName);
};

const clearHost = () => {
  form.hostId = "";
  form.hostName = "";
};

const onCloseDialog = useDebounceFn(() => emit("close"), 800);

const onCancelDialog = () => {
  editTimelineFormRef.value?.clearValidate();
  editTimelineFormRef.value?.resetFields();
  Object.assign(form, formOriginal);

  Object.keys(fieldValues).forEach((fieldId) => {
    fieldValues[fieldId] = fieldValuesOriginal[fieldId];
  });

  onCloseDialog();
};

const createTimelinePayload = () => {
  const payload: UpdateVisitorPayload = {
    full_name: form.name,
    company_name: form.companyName,
    user_id: form.hostId ? form.hostId : "0",
    ...Object.entries(fieldValues).reduce((fieldPayload, [fieldId, value]) => {
      const field = getVisitorFields.data.value?.find(
        (field) => field.numericId === Number(fieldId)
      );

      if (field?.fieldType && !UNSUPPORTED_FIELDS.includes(field.fieldType)) {
        return {
          ...fieldPayload,
          [`custom_${field.numericId}`]: value,
        };
      }

      return fieldPayload;
    }, {}),
    metadata: JSON.stringify(form.metadata),
  };

  return payload;
};

const saveChanges = async () => {
  try {
    await editTimelineFormRef.value?.validate();
  } catch (err) {
    return;
  }

  saveLoading.value = SAVELOADING.PENDING;

  if (!props.visit) return;

  updateVisitorAsync(
    {
      pk: props.visit.id,
      payload: createTimelinePayload(),
    },
    {
      onError: () => {
        sendErrorNotification(t("errors.updateVisitorFailed"), true);
        saveLoading.value = SAVELOADING.CLEAR;
      },
    }
  );
};

const handleUpdateMetadata = (payload: { notes: string }) => {
  form.metadata = payload;
};

const visitTime = (date?: Date) => {
  if (!date) return t("emptyValue");

  return formatDateTime(date);
};

watch(
  [() => props.visit, locations, categories],
  ([newVisit, newLocations, newCategories]) => {
    form.name = newVisit?.fullName ?? "";

    const location = newLocations.find(
      (location) => location.id === newVisit?.locationId
    );

    form.locationPk = location?.id ?? "";
    form.categoryName = newVisit?.categoryName ?? "";
    form.category =
      newCategories.find((category) => category.name === form.categoryName)
        ?.id ?? "";
    form.locationName = location?.name ?? "";
    form.selectHost =
      newVisit?.hostId && newVisit?.host?.firstName !== "Company"
        ? newVisit?.hostId !== null
        : false;
    form.hostName =
      form.selectHost &&
      newVisit?.host &&
      newVisit?.host?.firstName !== "Company"
        ? formatFullName(newVisit?.host.firstName, newVisit?.host.lastName)
        : "";
    form.hostId = form.selectHost ? newVisit?.hostId ?? "" : "";
    form.companyName = newVisit?.companyName ?? "";
    form.metadata.notes = newVisit?.notes ?? "";

    newVisit?.fields.forEach((field) => {
      if (!field.fieldId) return;
      if (field.fieldType === VisitFieldTypes.Multiselect) {
        fieldValues[field.fieldId] = field.value ? field.value : [];
        return;
      }

      if (field.fieldType === VisitFieldTypes.Document) {
        fieldValues[field.fieldId] = field.url ? [field.url, field.url] : [];
        return;
      }

      if (field.fieldType === VisitFieldTypes.Dob) {
        fieldValues[field.fieldId] = field.value
          ? dayjs.utc(field.value).toISOString()
          : "";
        return;
      }

      if (!field.value) {
        fieldValues[field.fieldId] = "";
        return;
      }

      fieldValues[field.fieldId] = field.value;
    });

    const fields = newVisit?.fields.map((field) => ({
      active: false,
      fieldOptions:
        field.fieldType === VisitFieldTypes.Multichoice ||
        field.fieldType === VisitFieldTypes.Multiselect
          ? Array.isArray(field.value)
            ? field.value
            : [field.value]
          : [],
      fieldType: field.fieldType,
      id: field.fieldId,
      numericId: Number(field.fieldId),
      required: false,
      settings: {},
      title: field.name,
    }));

    Object.assign(visitFields, cloneDeep(fields));
    Object.assign(formOriginal, cloneDeep(form));
    Object.assign(fieldValuesOriginal, cloneDeep(fieldValues));
  },
  { immediate: true }
);

watch(saveDialogVisible, (newVal) =>
  rootStore.setHasUnsavedFormChanges(newVal)
);

watch(locale, () => {
  const fieldRefs = {
    nameRef: nameRef.value,
    companyNameRef: companyNameRef.value,
  };
  validateFormItem(fieldRefs);
});

defineExpose({
  isAnonymized,
});
</script>

<template>
  <EditVisitorTimelineFormSkeleton
    :loading="isLoading"
    :has-title="false"
    :has-host="form.selectHost"
  >
    <el-divider />
    <div class="in-and-out">
      <h4>{{ t("visitors.timeline.inAndOut") }}</h4>
      <ReadOnlyField :label="t('visitors.timeline.signedIn')">
        <strong>
          <span>{{ visitTime(visit?.startedAt) }}</span>
        </strong>
        <div v-if="visit?.startedAt">
          {{
            visit?.sourceIn
              ? t("visitors.timeline.locationAndMethod", {
                  location: visit?.location?.name,
                  method: t(`visitors.timeline.methods.${visit?.sourceIn}`),
                })
              : visit?.location?.name
          }}
        </div>
      </ReadOnlyField>
      <ReadOnlyField :label="t('visitors.timeline.signedOut')">
        <strong>
          <span>{{ visitTime(visit?.finishedAt) }}</span>
        </strong>
        <div v-if="visit?.finishedAt">
          {{
            visit?.sourceOut
              ? t("visitors.timeline.locationAndMethod", {
                  location: visit?.location?.name,
                  method: t(`visitors.timeline.methods.${visit?.sourceOut}`),
                })
              : visit?.location?.name
          }}
        </div>
      </ReadOnlyField>
      <ReadOnlyField
        :label="t('visitors.timeline.totalTime')"
        :content="duration(visit)"
      />
    </div>
    <el-form
      v-if="!isAnonymized"
      ref="editTimelineFormRef"
      label-position="top"
      :model="form"
      :rules="rules"
      scroll-to-error
      :scroll-into-view-options="{ behavior: 'smooth', block: 'center' }"
    >
      <el-divider />

      <el-form-item
        ref="nameRef"
        :label="t('visitors.timeline.fullName')"
        prop="name"
      >
        <el-input
          v-model="form.name"
          :placeholder="t('visitors.timeline.namePlaceholder')"
          maxlength="128"
        />
      </el-form-item>

      <el-form-item
        :label="t('visitors.timeline.location')"
        prop="locationPk"
      >
        <el-select
          v-model="form.locationPk"
          value-key="pk"
          disabled
        >
          <el-option
            v-for="item in locations"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        :label="t('visitors.timeline.category')"
        class="filter-form-item"
        prop="categoryName"
      >
        <el-select
          v-model="form.categoryName"
          disabled
        >
          <el-option
            v-for="category in categories"
            :key="category.id"
            :value="category?.id ?? STATUS.ALL"
            :label="category.name"
          />
        </el-select>
      </el-form-item>

      <HostSelector
        v-if="hasHostSelection"
        v-model="form.selectHost"
        :host-name="form.hostName"
        :location-id="form.locationPk"
        :label="t('visitors.timeline.host')"
        @selected-host="setHost"
        @clear-host="clearHost"
      />

      <el-form-item
        v-if="companyNameEnabled"
        ref="companyNameRef"
        :label="settings.company_field_label"
        prop="companyName"
      >
        <el-input
          v-model="form.companyName"
          :disabled="!selectedCategory"
          maxlength="128"
        />
      </el-form-item>

      <VisitorFields
        required
        :is-preregistration="false"
        :location-id="form.locationPk"
        :category-id="form.category"
        :field-values="fieldValues"
        :fields="visitFields"
        :visitor-name="visit?.fullName"
        @update="Object.assign(fieldValues, $event)"
      />
    </el-form>

    <template v-else>
      <el-divider />
      <ReadOnlyField
        :label="t('visitors.timeline.location')"
        :content="form.locationName"
      />
      <ReadOnlyField
        :label="t('visitors.timeline.visitingHost')"
        :content="form.hostName"
      />
    </template>

    <VisitMetadata
      v-if="visit && !visit.anonymizedAt"
      :visit-with-fields="visit"
      :metadata="form.metadata"
      :selected-category="selectedCategory"
      :visit-type="TYPE_TIMELINE"
      @update-metadata="handleUpdateMetadata"
    />

    <InOutImage
      v-if="visit?.photoIn"
      class="in-out-image"
      :in-photo="visit?.photoIn"
    />
  </EditVisitorTimelineFormSkeleton>

  <SaveDialog
    :visible="saveDialogVisible"
    :save-loading="saveLoading"
    @cancel="onCancelDialog"
    @save="saveChanges"
    @close="emit('close')"
  />
</template>

<style scoped>
.so-select {
  width: 100%;
}

.in-and-out h4 {
  margin-bottom: 10px;
  font-size: 1rem;
}

.in-out-image {
  margin-top: 2rem;
}
</style>
